import {FC, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {getCookiesContent} from '@/content/cookies'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextVariant} from '../common/content/Text'
import {Modal} from '../common/modal/Modal'
import {CookiesSettings} from './CookiesSettings'

type CookiesModalProps = {
  isVisible: boolean
  otherAllowed: boolean
  saveConsent: (allowed: boolean) => void
  closeModal: () => void
}

export const CookiesModal: FC<React.PropsWithChildren<CookiesModalProps>> = ({
  isVisible,
  otherAllowed,
  saveConsent,
  closeModal,
}) => {
  const {t} = useTranslation()
  const language = useLanguage()
  const {title, buttonSaveTitle, buttonAcceptTitle} = getCookiesContent(language).modal
  const [otherCookiesAllowed, setOtherCookiesAllowed] = useState(otherAllowed)

  const changeOtherCookiesAllowed = (value: boolean) => {
    setOtherCookiesAllowed(value)
    saveConsent(value)
    closeModal()
  }

  return (
    <>
      <Modal isVisible={isVisible} closeModal={closeModal} hasPadding={false}>
        <div className="content">
          <div className="header">
            <Text variant={TextVariant.h4} element="h2">
              {t(title)}
            </Text>
          </div>
          <div className="body">
            <CookiesSettings
              otherAllowed={otherCookiesAllowed}
              setOtherAllowed={setOtherCookiesAllowed}
            />
          </div>
          <div className="footer">
            <div className="button-wrapper">
              <Button
                variant={ButtonVariant.secondary}
                type="button"
                fullWidth
                onClick={() => changeOtherCookiesAllowed(otherCookiesAllowed)}
              >
                {t(buttonSaveTitle)}
              </Button>
            </div>
            <div className="button-wrapper">
              <Button
                variant={ButtonVariant.primary}
                type="button"
                fullWidth
                onClick={() => changeOtherCookiesAllowed(true)}
              >
                {t(buttonAcceptTitle)}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <style jsx>{`
        .content {
          max-width: 700px;
        }
        .header {
          border: 4px solid ${theme.palette.butter[100]};
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          padding: ${theme.spacing[4]};
          border-radius: 16px 16px 0 0;
        }
        .body {
          border-left: 4px solid ${theme.palette.butter[100]};
          border-right: 4px solid ${theme.palette.butter[100]};
          padding: ${theme.spacing[5]} ${theme.spacing[5]} 0;
        }
        .footer {
          display: flex;
          justify-content: center;
          gap: ${theme.spacing[3]};
          padding: ${theme.spacing[4]} ${theme.spacing[5]};
          border: 4px solid ${theme.palette.butter[100]};
          border-top: 0;
          border-radius: 0 0 16px 16px;
        }
        .button-wrapper {
          width: 100%;
        }
        @media ${MOBILE_SCREENSIZE} {
          .header {
            padding: ${theme.spacing[3]};
          }
          .body {
            padding: ${theme.spacing[3]} ${theme.spacing[3]} 0;
            max-height: 300px;
            overflow-y: auto;
          }
          .footer {
            flex-direction: column;
            align-items: center;
            padding: ${theme.spacing[3]};
            gap: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
